import { useCallback, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';
import type { ViewState } from '../shared/types';
import type { Place } from '../types/warehouse.types';
import {
  type BenchmarkOperation,
  createPanOperation,
  createRapidZoomOperation,
  createRotationOperation,
  createZoomOperation,
} from '../utils/BenchmarkOperations';
import {
  type BenchmarkResults,
  MetricsCollector,
} from '../utils/MetricsCollector';
import { delay, selectRandomPlaces } from '../utils/placeSelectionUtils';

export interface UseBenchmarkProps {
  viewState: ViewState;
  setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
  places: Map<string, Place>;
  canvasSize: { width: number; height: number };
  setSelectedPlaceIds: (ids: Set<string>) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  handleRotateLeftMouseDown: () => void;
  handleRotateRightMouseDown: () => void;
  handleRotateMouseUp: () => void;
}

export interface BenchmarkState {
  status: 'idle' | 'running' | 'completed' | 'error';
  currentOperation: string | null;
  progress: number;
  error: string | null;
}

const DEFAULT_BENCHMARK_CONFIG = {
  rotationDuration: 5240,      // Matches useRotation (0.02 rad/frame @ 60fps)
  zoomDuration: 3000,          // For smooth zoom
  rapidZoomSteps: 20,          // For rapid zoom
  rapidZoomStepDelay: 200,     // For rapid zoom (200ms between steps)
  panDuration: 3000,
  panDistance: 500,
  selectedPlaceCount: 10,
};

export const useBenchmark = ({
  viewState,
  setViewState,
  places,
  canvasSize,
  setSelectedPlaceIds,
  zoomIn,
  zoomOut,
  handleRotateLeftMouseDown,
  handleRotateRightMouseDown,
  handleRotateMouseUp,
}: UseBenchmarkProps) => {
  const [benchmarkState, setBenchmarkState] = useState<BenchmarkState>({
    status: 'idle',
    currentOperation: null,
    progress: 0,
    error: null,
  });

  const translate = useTranslate();
  const [benchmarkResults, setBenchmarkResults] = useState<BenchmarkResults | null>(null);
  const metricsCollectorRef = useRef<MetricsCollector | null>(null);
  const originalViewStateRef = useRef<ViewState | null>(null);

  const startBenchmark = useCallback(async () => {
    try {
      // Store original view state
      originalViewStateRef.current = { ...viewState };

      // Initialize metrics collector
      metricsCollectorRef.current = new MetricsCollector(DEFAULT_BENCHMARK_CONFIG);
      metricsCollectorRef.current.startBenchmark();

      // Set running state
      setBenchmarkState({
        status: 'running',
        currentOperation: null,
        progress: 0,
        error: null,
      });

      // Select random places for selection tests
      const selectedPlaces = selectRandomPlaces(places, DEFAULT_BENCHMARK_CONFIG.selectedPlaceCount);

      // Define operations sequence
      const operations: BenchmarkOperation[] = [
        createRotationOperation(false, undefined, translate),
        createZoomOperation(false, undefined, translate),
        createRapidZoomOperation(false, undefined, translate),
        createPanOperation(false, undefined, translate),
        createRotationOperation(true, selectedPlaces, translate),
        createZoomOperation(true, selectedPlaces, translate),
        createRapidZoomOperation(true, selectedPlaces, translate),
        createPanOperation(true, selectedPlaces, translate),
      ];

      // Execute each operation
      for (let i = 0; i < operations.length; i++) {
        const operation = operations[i];

        setBenchmarkState((prev) => ({
          ...prev,
          currentOperation: operation.name,
          progress: (i / operations.length) * 100,
        }));

        // Start metrics collection for this operation
        metricsCollectorRef.current.startOperation(operation.id, operation.name);

        // Execute the operation
        await operation.execute({
          viewState,
          setViewState,
          places,
          canvasSize,
          setSelectedPlaceIds,
          config: DEFAULT_BENCHMARK_CONFIG,
          zoomIn,
          zoomOut,
          handleRotateLeftMouseDown,
          handleRotateRightMouseDown,
          handleRotateMouseUp,
        });

        // End metrics collection for this operation
        metricsCollectorRef.current.endOperation(operation.id);

        // Small delay between operations
        await delay(500);
      }

      // Get results
      const results = metricsCollectorRef.current.getResults();
      setBenchmarkResults(results);

      // Restore original view state
      if (originalViewStateRef.current) {
        setViewState(originalViewStateRef.current);
      }

      // Clear selection
      setSelectedPlaceIds(new Set());

      // Set completed state
      setBenchmarkState({
        status: 'completed',
        currentOperation: null,
        progress: 100,
        error: null,
      });

      // Cleanup
      metricsCollectorRef.current.cleanup();
    } catch (error) {
      console.error('Benchmark error:', error);
      setBenchmarkState({
        status: 'error',
        currentOperation: null,
        progress: 0,
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      // Restore original view state on error
      if (originalViewStateRef.current) {
        setViewState(originalViewStateRef.current);
      }

      // Clear selection
      setSelectedPlaceIds(new Set());

      // Cleanup
      if (metricsCollectorRef.current) {
        metricsCollectorRef.current.cleanup();
      }
    }
  }, [viewState, setViewState, places, canvasSize, setSelectedPlaceIds]);

  const resetBenchmark = useCallback(() => {
    setBenchmarkState({
      status: 'idle',
      currentOperation: null,
      progress: 0,
      error: null,
    });
    setBenchmarkResults(null);
  }, []);

  return {
    startBenchmark,
    resetBenchmark,
    benchmarkState,
    benchmarkResults,
  };
};
